import React, { useState, useEffect } from "react";
import { useNavigate, Link } from "react-router-dom";
import axios from "axios";
import Header from "@/components/Header";
import Footer from "@/components/Footer";
import { useCustomer } from "@/context/CustomerContext";
import { toast } from "sonner";
import { User, Gift, History, LogOut, Star, ShoppingBag, ChevronRight, Settings, Megaphone, ShieldCheck } from "lucide-react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const CustomerProfilePage = () => {
  const navigate = useNavigate();
  const { customer, logout, refreshCustomer, isLoggedIn, loading } = useCustomer();
  const [rewards, setRewards] = useState([]);
  const [orders, setOrders] = useState([]);
  const [pointsHistory, setPointsHistory] = useState([]);
  const [activeTab, setActiveTab] = useState("orders"); // Default su ordini
  const [loadingData, setLoadingData] = useState(true);
  const [togglingMarketing, setTogglingMarketing] = useState(false);

  // Affiliati partner → interfaccia privata
  const isPartnerAffiliated = !!customer?.partner_company_id;
  const isBusiness = customer?.account_type === "business" && !isPartnerAffiliated;
  const isPrivateUI = !isBusiness;
  const isSpecialCustomer = !!customer?.is_special_customer;

  useEffect(() => {
    if (!loading && !isLoggedIn) {
      navigate("/accedi");
    }
  }, [loading, isLoggedIn, navigate]);

  // Imposta tab default in base al tipo account
  useEffect(() => {
    if (customer) {
      if (isBusiness) {
        setActiveTab("orders");
      } else {
        setActiveTab("rewards");
      }
    }
  }, [customer]);

  useEffect(() => {
    if (isLoggedIn) {
      fetchData();
    }
  }, [isLoggedIn]);

  const fetchData = async () => {
    const token = localStorage.getItem("customer_token");
    const headers = { Authorization: `Bearer ${token}` };

    try {
      const [rewardsRes, ordersRes, historyRes] = await Promise.all([
        axios.get(`${API}/rewards`),
        axios.get(`${API}/customer/orders`, { headers }),
        axios.get(`${API}/customer/points-history`, { headers })
      ]);

      setRewards(rewardsRes.data);
      setOrders(ordersRes.data);
      setPointsHistory(historyRes.data);
    } catch (error) {
      console.error("Errore caricamento dati:", error);
    } finally {
      setLoadingData(false);
    }
  };

  const handleRedeemReward = async (rewardId) => {
    const token = localStorage.getItem("customer_token");
    
    try {
      const response = await axios.post(
        `${API}/rewards/redeem`,
        { reward_id: rewardId },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      toast.success(response.data.message);
      
      if (response.data.redemption_code) {
        toast.success(`Codice premio: ${response.data.redemption_code}`, { duration: 10000 });
      }
      
      // Aggiorna i dati
      await refreshCustomer();
      fetchData();
    } catch (error) {
      const errorMsg = error.response?.data?.detail || "Errore nel riscatto";
      toast.error(errorMsg);
    }
  };

  const handleLogout = () => {
    logout();
    toast.success("Logout effettuato");
    navigate("/");
  };

  const handleToggleMarketing = async () => {
    setTogglingMarketing(true);
    const token = localStorage.getItem("customer_token");
    try {
      const newValue = !customer.consent_marketing;
      await axios.put(`${API}/customer/me`, 
        { consent_marketing: newValue },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      await refreshCustomer();
      toast.success(newValue 
        ? "Comunicazioni promozionali attivate" 
        : "Comunicazioni promozionali disattivate"
      );
    } catch (error) {
      toast.error("Errore nell'aggiornamento delle preferenze");
    } finally {
      setTogglingMarketing(false);
    }
  };

  if (loading || !customer) {
    return (
      <div className="min-h-screen flex flex-col bg-black">
        <Header />
        <main className="flex-1 flex items-center justify-center">
          <div className="w-12 h-12 border-4 border-green-500 border-t-transparent rounded-full animate-spin"></div>
        </main>
        <Footer />
      </div>
    );
  }

  return (
    <div className="min-h-screen flex flex-col">
      <Header />
      
      <main className="flex-1 max-w-5xl mx-auto px-4 py-8 w-full">
        {/* Profile Header */}
        <div className="bg-zinc-900 border border-zinc-800 rounded-lg p-6 mb-6">
          <div className="flex items-center justify-between flex-wrap gap-4">
            <div className="flex items-center gap-4">
              <div className="w-16 h-16 bg-green-500 rounded-full flex items-center justify-center">
                <User className="w-8 h-8 text-black" />
              </div>
              <div>
                <h1 className="text-2xl font-bold text-white">{customer.name}</h1>
                <p className="text-zinc-400">{customer.email}</p>
              </div>
            </div>
            <button
              onClick={handleLogout}
              className="flex items-center gap-2 text-red-500 hover:text-red-400 transition-colors"
            >
              <LogOut className="w-5 h-5" />
              Esci
            </button>
          </div>
          
          {/* Points Display - Solo per privati non speciali */}
          {isPrivateUI && !isSpecialCustomer && (
            <div className="mt-6 bg-gradient-to-r from-green-600 to-green-500 rounded-lg p-6 text-center">
              <div className="flex items-center justify-center gap-2 mb-2">
                <Star className="w-8 h-8 text-yellow-300" />
                <span className="text-5xl font-bold text-black">{customer.points || 0}</span>
              </div>
              <p className="text-black font-semibold">Punti Fedeltà</p>
              <p className="text-black/70 text-sm mt-1">1 punto per ogni euro speso</p>
            </div>
          )}

          {/* Badge Azienda */}
          {isBusiness && (
            <div className="mt-6 bg-gradient-to-r from-blue-600 to-blue-500 rounded-lg p-6 text-center">
              <p className="text-white font-bold text-lg">Account Aziendale</p>
              {customer.business_data?.company_name && (
                <p className="text-white/80 mt-1">{customer.business_data.company_name}</p>
              )}
              <p className="text-white/60 text-sm mt-2">
                Pagando con carta: 5% sconto monoporzione • 2,5% gastronomia
              </p>
            </div>
          )}

          {/* Banner La Mia Lista - Solo per clienti speciali */}
          {isSpecialCustomer && (
            <div className="mt-6 bg-gradient-to-r from-yellow-600 to-yellow-500 rounded-lg p-6 cursor-pointer hover:opacity-90 transition-opacity" onClick={() => navigate("/la-mia-lista")}>
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-black font-bold text-lg flex items-center gap-2">
                    <ShoppingBag className="w-6 h-6" />
                    La Mia Lista
                  </p>
                  <p className="text-black/70 text-sm mt-1">Vai alla tua lista della spesa per ordinare</p>
                </div>
                <ChevronRight className="w-6 h-6 text-black" />
              </div>
            </div>
          )}
        </div>

        {/* Tabs */}
        <div className="flex gap-2 mb-6 overflow-x-auto">
          {isPrivateUI && !isSpecialCustomer && (
            <button
              onClick={() => setActiveTab("rewards")}
              className={`flex items-center gap-2 px-4 py-2 rounded-lg font-semibold transition-all whitespace-nowrap ${
                activeTab === "rewards"
                  ? "bg-green-500 text-black"
                  : "bg-zinc-800 text-zinc-300 hover:bg-zinc-700"
              }`}
            >
              <Gift className="w-5 h-5" />
              Premi
            </button>
          )}
          <button
            onClick={() => setActiveTab("orders")}
            className={`flex items-center gap-2 px-4 py-2 rounded-lg font-semibold transition-all whitespace-nowrap ${
              activeTab === "orders"
                ? "bg-green-500 text-black"
                : "bg-zinc-800 text-zinc-300 hover:bg-zinc-700"
            }`}
          >
            <ShoppingBag className="w-5 h-5" />
            I miei ordini
          </button>
          {isPrivateUI && (
            <button
              onClick={() => setActiveTab("history")}
              className={`flex items-center gap-2 px-4 py-2 rounded-lg font-semibold transition-all whitespace-nowrap ${
                activeTab === "history"
                  ? "bg-green-500 text-black"
                  : "bg-zinc-800 text-zinc-300 hover:bg-zinc-700"
              }`}
            >
              <History className="w-5 h-5" />
              Storico punti
            </button>
          )}
          <button
            onClick={() => setActiveTab("settings")}
            className={`flex items-center gap-2 px-4 py-2 rounded-lg font-semibold transition-all whitespace-nowrap ${
              activeTab === "settings"
                ? "bg-green-500 text-black"
                : "bg-zinc-800 text-zinc-300 hover:bg-zinc-700"
            }`}
            data-testid="settings-tab"
          >
            <Settings className="w-5 h-5" />
            Impostazioni
          </button>
        </div>

        {/* Tab Content */}
        {activeTab === "rewards" && isPrivateUI && !isSpecialCustomer && (
          <div className="space-y-4">
            <h2 className="text-xl font-bold text-white mb-4">Prodotto Omaggio</h2>
            
            <div className={`bg-zinc-900 border rounded-lg p-6 ${
              customer.points >= 250 ? "border-green-500" : "border-zinc-800"
            }`}>
              <div className="flex justify-between items-start mb-3">
                <div className="flex items-center gap-3">
                  <Gift className="w-8 h-8 text-yellow-500" />
                  <div>
                    <h3 className="text-lg font-bold text-white">Prodotto a scelta GRATIS</h3>
                    <p className="text-zinc-400 text-sm">Scegli un prodotto omaggio quando fai un ordine</p>
                  </div>
                </div>
                <span className={`px-3 py-1 rounded-full text-sm font-bold ${
                  customer.points >= 250 
                    ? "bg-green-500 text-black" 
                    : "bg-zinc-700 text-zinc-300"
                }`}>
                  250 punti
                </span>
              </div>
              
              {customer.points >= 250 ? (
                <div className="bg-green-500/10 border border-green-500 rounded-lg p-4 mt-4">
                  <p className="text-green-500 font-semibold text-center">
                    Hai {customer.points} punti! Puoi ottenere un prodotto omaggio al prossimo ordine.
                  </p>
                  <Link
                    to="/catalogo"
                    className="block w-full bg-green-500 text-black py-3 rounded-lg font-semibold text-center mt-3 hover:bg-green-600 transition-all"
                  >
                    Vai al Catalogo
                  </Link>
                </div>
              ) : (
                <div className="text-center text-zinc-400 mt-4 p-4 bg-zinc-800/50 rounded-lg">
                  <p>Ti mancano <span className="text-yellow-500 font-bold">{250 - customer.points}</span> punti</p>
                  <p className="text-sm mt-1">Continua a fare ordini per accumulare punti!</p>
                </div>
              )}
            </div>
            
            <div className="bg-zinc-800/50 rounded-lg p-4 mt-4">
              <p className="text-zinc-400 text-sm">
                <strong className="text-white">Come funziona:</strong> Accumula 250 punti (1€ = 1 punto) e al checkout potrai scegliere un prodotto omaggio a tua scelta da aggiungere al tuo ordine!
              </p>
            </div>
          </div>
        )}

        {activeTab === "orders" && (
          <div className="space-y-4">
            <h2 className="text-xl font-bold text-white mb-4">I Miei Ordini</h2>
            {orders.length === 0 ? (
              <div className="text-center py-8">
                <ShoppingBag className="w-16 h-16 text-zinc-600 mx-auto mb-4" />
                <p className="text-zinc-400">Non hai ancora effettuato ordini.</p>
                <Link
                  to="/catalogo"
                  className="inline-block mt-4 bg-green-500 text-black px-6 py-2 rounded-lg font-semibold hover:bg-green-600 transition-all"
                >
                  Vai al catalogo
                </Link>
              </div>
            ) : (
              <div className="space-y-3">
                {orders.map((order) => (
                  <div
                    key={order.id}
                    className="bg-zinc-900 border border-zinc-800 rounded-lg p-4 hover:border-zinc-700 transition-all"
                  >
                    <div className="flex justify-between items-center">
                      <div>
                        <p className="text-white font-semibold">{order.order_number}</p>
                        <p className="text-zinc-400 text-sm">
                          {new Date(order.created_at).toLocaleDateString("it-IT", {
                            day: "numeric",
                            month: "long",
                            year: "numeric"
                          })}
                        </p>
                      </div>
                      <div className="text-right">
                        <p className="text-white font-bold">€{order.total?.toFixed(2)}</p>
                        <span className={`text-sm px-2 py-1 rounded ${
                          order.status === "Consegnato" 
                            ? "bg-green-500/20 text-green-500"
                            : order.status === "Nuovo"
                            ? "bg-yellow-500/20 text-yellow-500"
                            : "bg-zinc-700 text-zinc-300"
                        }`}>
                          {order.status}
                        </span>
                      </div>
                    </div>
                    {order.points_earned > 0 && isPrivateUI && (
                      <p className="text-green-500 text-sm mt-2">
                        +{order.points_earned} punti guadagnati
                      </p>
                    )}
                  </div>
                ))}
              </div>
            )}
          </div>
        )}

        {activeTab === "history" && isPrivateUI && (
          <div className="space-y-4">
            <h2 className="text-xl font-bold text-white mb-4">Storico Punti</h2>
            {pointsHistory.length === 0 ? (
              <p className="text-zinc-400">Nessun movimento punti.</p>
            ) : (
              <div className="space-y-2">
                {pointsHistory.map((entry) => (
                  <div
                    key={entry.id}
                    className="bg-zinc-900 border border-zinc-800 rounded-lg p-4 flex justify-between items-center"
                  >
                    <div>
                      <p className="text-white">{entry.description}</p>
                      <p className="text-zinc-500 text-sm">
                        {new Date(entry.created_at).toLocaleDateString("it-IT")}
                      </p>
                    </div>
                    <span className={`font-bold ${
                      entry.points > 0 ? "text-green-500" : "text-red-500"
                    }`}>
                      {entry.points > 0 ? "+" : ""}{entry.points}
                    </span>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}

        {activeTab === "settings" && (
          <div className="space-y-6">
            <h2 className="text-xl font-bold text-white mb-4">Impostazioni</h2>
            
            {/* Privacy */}
            <div className="bg-zinc-900 border border-zinc-800 rounded-lg p-6">
              <div className="flex items-center gap-3 mb-4">
                <ShieldCheck className="w-6 h-6 text-green-500" />
                <h3 className="text-lg font-semibold text-white">Privacy</h3>
              </div>
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-zinc-300">Privacy Policy</p>
                  <p className="text-zinc-500 text-sm">
                    Accettata {customer.consent_privacy_date ? `il ${new Date(customer.consent_privacy_date).toLocaleDateString("it-IT")}` : ""}
                    {customer.privacy_policy_version && ` (${customer.privacy_policy_version})`}
                  </p>
                </div>
                <span className="bg-green-500/20 text-green-500 px-3 py-1 rounded-full text-sm font-medium">
                  Accettata
                </span>
              </div>
              <Link
                to="/privacy-policy"
                className="text-green-500 hover:underline text-sm mt-3 inline-block"
                data-testid="settings-privacy-link"
              >
                Leggi la Privacy Policy
              </Link>
            </div>

            {/* Marketing */}
            <div className="bg-zinc-900 border border-zinc-800 rounded-lg p-6">
              <div className="flex items-center gap-3 mb-4">
                <Megaphone className="w-6 h-6 text-yellow-500" />
                <h3 className="text-lg font-semibold text-white">Comunicazioni Promozionali</h3>
              </div>
              <div className="flex items-center justify-between">
                <div className="flex-1 mr-4">
                  <p className="text-zinc-300">Ricevi offerte, sconti e novità</p>
                  <p className="text-zinc-500 text-sm mt-1">
                    {customer.consent_marketing 
                      ? `Attivato ${customer.consent_marketing_date ? `il ${new Date(customer.consent_marketing_date).toLocaleDateString("it-IT")}` : ""}`
                      : "Disattivato"
                    }
                  </p>
                </div>
                <button
                  onClick={handleToggleMarketing}
                  disabled={togglingMarketing}
                  className={`relative w-14 h-8 rounded-full transition-colors duration-300 ${
                    customer.consent_marketing ? "bg-green-500" : "bg-zinc-700"
                  } ${togglingMarketing ? "opacity-50 cursor-not-allowed" : "cursor-pointer"}`}
                  data-testid="marketing-toggle"
                  aria-label="Toggle comunicazioni promozionali"
                >
                  <span className={`absolute top-1 w-6 h-6 bg-white rounded-full shadow transition-transform duration-300 ${
                    customer.consent_marketing ? "translate-x-7" : "translate-x-1"
                  }`} />
                </button>
              </div>
            </div>
          </div>
        )}
      </main>

      <Footer />
    </div>
  );
};

export default CustomerProfilePage;
